import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { LayoutGrid, ArrowLeft, Calendar, Users, Info, Clock, ChevronRight, Briefcase, Wallet } from "lucide-react";
import KanbanBoard from "../../components/kanban-board/kanban-board";
import { projectAPI } from "../../services/api";
import { useWallet } from "../../context/WalletContext";

// Custom CSS to hide scrollbars while maintaining scroll functionality
const customScrollbarStyle = `
  /* Hide scrollbar for Chrome, Safari and Opera */
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  .hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }

  /* Add visual indicators for scroll shadow */
  .scroll-shadow-right {
    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.15);
  }
  
  .scroll-shadow-left {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.15);
  }
  
  .scroll-shadow-both {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.15), inset -10px 0 8px -8px rgba(0, 0, 0, 0.15);
  }

  /* Full height kanban board */
  .kanban-container {
    min-height: calc(100vh - 240px);
  }
`;

export const TasksDetails = () => {
  const { search } = useLocation();
  const projectId = new URLSearchParams(search).get("id");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isConnected } = useWallet();

  // Fetch basic project info for enhanced header
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) return;
      try {
        setLoading(true);
        const { data } = await projectAPI.getByIdPublic(projectId);
        setProject(data);
      } catch (error) {
        console.error("Error fetching project details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectInfo();
  }, [projectId]);

  return (
    <div className="flex flex-col w-full bg-gray-50 min-h-screen">
      {/* Inject custom CSS for scrollbar styling */}
      <style>{customScrollbarStyle}</style>
      
      {/* Enhanced header with gradient background */}
      <header className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white shadow-md relative overflow-hidden">
        {/* Background pattern overlay */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        
        <div className="w-full mx-auto px-4 py-6 relative z-10">
          <div className="flex flex-col space-y-4">
            {/* Navigation and title */}
            <div className="flex justify-between items-center">
              <div>
                {projectId && (
                  <Link
                    to={`/projects/token/${projectId}`}
                    className="mb-2 inline-flex items-center text-sm text-white/90 hover:text-white group bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-sm transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1.5 transition-transform group-hover:-translate-x-1" />
                    Back to Project Details
                  </Link>
                )}
                <h1 className="text-3xl font-bold mt-2 flex items-center">
                  {project ? (
                    <>
                      <span>{project.title || project.name}</span>
                      <ChevronRight className="h-5 w-5 mx-2 text-white/70" />
                      <span className="text-white/90 font-normal">Task Board</span>
                    </>
                  ) : (
                    "Task Board"
                  )}
                </h1>
              </div>
              <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-sm backdrop-blur-sm">
                <LayoutGrid className="h-4 w-4" />
                <span>Kanban View</span>
              </div>
            </div>
            
            {/* Wallet connection status */}
            {!isConnected && (
              <div className="bg-white/20 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm mt-2">
                <Wallet className="h-5 w-5 text-white/80" />
                <p className="text-sm text-white/90">
                  You can view tasks without connecting your wallet, but you'll need to connect to create, claim, or interact with tasks.
                </p>
              </div>
            )}

            {/* Project metadata if available */}
            {project && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 pt-3 border-t border-white/20">
                <div className="flex items-center gap-2 text-white/80">
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  <span>Created: {new Date(project.createdAt).toLocaleDateString()}</span>
                </div>
                
                {project.description && (
                  <div className="flex items-center gap-2 text-white/80 truncate">
                    <Info className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{project.description.substring(0, 60)}{project.description.length > 60 ? '...' : ''}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content with enhanced Kanban board container - full width and increased height */}
      <main className="flex-1 w-full mx-auto px-2 sm:px-3 py-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 mb-4 max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                <Briefcase className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Task Board</h2>
                <p className="text-sm text-gray-500 mt-1">Organize and manage project tasks with drag-and-drop</p>
              </div>
            </div>
            
          </div>
          
          {/* Enhanced Kanban board container with visual scroll indicators - full width and increased height */}
          <div className="overflow-hidden rounded-lg border border-gray-100 kanban-container">
            <KanbanBoard projectId={projectId} />
          </div>
        </div>
      </main>

      {/* Simple footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-4 text-center text-gray-500 text-sm">
        <div className="w-full mx-auto px-4">
          Manage your project tasks efficiently with our Kanban Board
        </div>
      </footer>
    </div>
  );
};
